import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  CalendarDays, 
  Loader2,
  Clock,
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckCircle,
  AlertCircle,
  Send,
  Building,
  Users,
  Eye,
  RefreshCw,
  XCircle,
  User,
  FileText,
  CalendarRange,
  Layers,
  Trash2,
  Edit,
  Upload,
  Download,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper for board panel
const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BoardIsPlan = () => {
  const { branchId } = useParams();
  const { branches } = useOutletContext();
  const [activeTab, setActiveTab] = useState('yonetim');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProgram, setNewProgram] = useState({
    baslik: '',
    aciklama: '',
    tarih: new Date().toISOString().split('T')[0],
    hedef: 'sube'
  });

  // ========== ŞUBE İŞ PROGRAMI STATE'LERİ (BranchAssignments ile birebir aynı) ==========
  const [subeMainTab, setSubeMainTab] = useState('daily');
  const [subeSchedules, setSubeSchedules] = useState([]);
  const [subeLoading, setSubeLoading] = useState(false);
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const branch = branches?.find(b => b.id === branchId);
  const branchName = branch?.name || 'Şube';

  useEffect(() => {
    fetchPlans();
    fetchPrograms();
  }, [branchId]);

  // Şube iş programlarını yükle (tab değiştiğinde)
  useEffect(() => {
    if (activeTab === 'sube') {
      loadSubeSchedules();
    }
  }, [activeTab, subeMainTab, branchId]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/board/branch/${branchId}/is-plani`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('İş planları yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/board/branch/${branchId}/is-programi`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('İş programları yüklenemedi:', error);
      setPrograms([]);
    }
  };

  // ========== ŞUBE İŞ PROGRAMI FONKSİYONLARI ==========
  const loadSubeSchedules = async () => {
    setSubeLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/board/branch/${branchId}/work-schedules`, {
        params: { schedule_type: subeMainTab },
        headers: getAuthHeaders()
      });
      setSubeSchedules(res.data || []);
    } catch (error) {
      console.error('Şube iş planları yüklenirken hata:', error);
      toast.error('Şube iş planları yüklenemedi');
      setSubeSchedules([]);
    }
    setSubeLoading(false);
  };

  const loadProgressLogs = async (scheduleId) => {
    try {
      const res = await axios.get(`${API_URL}/api/work/schedules/${scheduleId}/progress`, { headers: getAuthHeaders() });
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Güncel durum yüklenemedi:', error);
    }
  };

  const handleOpenProgressModal = (schedule) => {
    setSelectedSchedule(schedule);
    loadProgressLogs(schedule.id);
    setProgressModalOpen(true);
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  // Sıralanmış veriler
  const sortedSchedules = React.useMemo(() => {
    if (!sortConfig.key) return subeSchedules;
    
    return [...subeSchedules].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'owner':
          aValue = (a.owner?.name || a.owner_name || '').toLowerCase();
          bValue = (b.owner?.name || b.owner_name || '').toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'accepted_by':
          aValue = a.accepted_by?.length || 0;
          bValue = b.accepted_by?.length || 0;
          break;
        case 'start_date':
          aValue = new Date(a.start_date || 0).getTime();
          bValue = new Date(b.start_date || 0).getTime();
          break;
        case 'priority':
          const priorityOrder = { high: 3, normal: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusOrder = { completed: 4, in_progress: 3, accepted: 2, pending: 1 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'completed_at':
          aValue = a.completed_at ? new Date(a.completed_at).getTime() : 0;
          bValue = b.completed_at ? new Date(b.completed_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [subeSchedules, sortConfig]);

  const isPreviewable = (type) => {
    return type && (type.startsWith('image/') || type === 'application/pdf');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadge = (status) => {
    const config = {
      planned: { label: 'Planlandı', color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_progress: { label: 'Devam Ediyor', color: 'bg-amber-100 text-amber-700', icon: RefreshCw },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config.planned;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Düşük', color: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', color: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', color: 'bg-red-100 text-red-600' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getTabIcon = (type) => {
    switch(type) {
      case 'daily': return CalendarDays;
      case 'monthly': return CalendarRange;
      case 'master': return Layers;
      default: return CalendarDays;
    }
  };

  const getTabLabel = (type) => {
    switch(type) {
      case 'daily': return 'GÜNLÜK İŞ PLANI';
      case 'monthly': return 'AYLIK İŞ PLANI';
      case 'master': return 'MASTER İŞ PLANI';
      default: return type;
    }
  };

  const handleAddProgram = async () => {
    if (!newProgram.baslik.trim()) {
      toast.error('Başlık giriniz');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/board/branch/${branchId}/is-programi`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newProgram,
          branch_id: branchId,
          gonderen: 'yonetim'
        })
      });
      
      if (response.ok) {
        toast.success('İş programı gönderildi');
        setShowAddModal(false);
        setNewProgram({ baslik: '', aciklama: '', tarih: new Date().toISOString().split('T')[0], hedef: 'sube' });
        fetchPrograms();
      } else {
        toast.error('Gönderme başarısız');
      }
    } catch (error) {
      console.error('İş programı gönderilemedi:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const selectedDatePlans = plans.filter(plan => {
    const planDate = new Date(plan.tarih);
    return planDate.toDateString() === selectedDate.toDateString();
  });

  const hasPlans = (date) => {
    return plans.some(plan => {
      const planDate = new Date(plan.tarih);
      return planDate.toDateString() === date.toDateString();
    });
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  // ========== ŞUBE İŞ PROGRAMI TABLO RENDER ==========
  const renderSubeScheduleTable = () => {
    if (sortedSchedules.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">İş Planı Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride iş planı yok</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center gap-1">
                    İş Planı
                    <SortIcon columnKey="title" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('owner')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturan
                    <SortIcon columnKey="owner" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturulma
                    <SortIcon columnKey="created_at" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('accepted_by')}
                >
                  <div className="flex items-center gap-1">
                    Kabul Edenler
                    <SortIcon columnKey="accepted_by" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('start_date')}
                >
                  <div className="flex items-center gap-1">
                    Tarih Aralığı
                    <SortIcon columnKey="start_date" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('priority')}
                >
                  <div className="flex items-center gap-1">
                    Öncelik
                    <SortIcon columnKey="priority" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Durum
                    <SortIcon columnKey="status" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('completed_at')}
                >
                  <div className="flex items-center gap-1">
                    Tamamlanma Zamanı
                    <SortIcon columnKey="completed_at" />
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSchedules.map(schedule => (
                <TableRow key={schedule.id} data-testid={`schedule-row-${schedule.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{schedule.title}</p>
                      {schedule.description && (
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">{schedule.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {schedule.owner?.name || schedule.owner_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-600">
                      {schedule.created_at 
                        ? new Date(schedule.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {schedule.accepted_by && schedule.accepted_by.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {schedule.accepted_by.map((a, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-green-50 text-green-700">
                            {a.user_name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {schedule.start_date ? (
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <CalendarDays className="h-3 w-3 text-gray-400" />
                          {new Date(schedule.start_date).toLocaleDateString('tr-TR')}
                        </div>
                        {schedule.end_date && (
                          <div className="text-gray-500">
                            → {new Date(schedule.end_date).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{getPriorityBadge(schedule.priority)}</TableCell>
                  <TableCell>{getStatusBadge(schedule.status)}</TableCell>
                  <TableCell>
                    {schedule.status === 'completed' && schedule.completion_date ? (
                      <div className="text-sm text-green-600">
                        {new Date(schedule.completion_date).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setSelectedSchedule(schedule); setDetailModalOpen(true); }}
                        data-testid={`view-schedule-${schedule.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenProgressModal(schedule)}
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  // Yönetim İş Programı Tab İçeriği
  const renderYonetimTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Yönetim İş Programı</h2>
          <p className="text-sm text-gray-500">Şube yönetimi ile karşılıklı iş programı paylaşımı</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Yeni İş Programı
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gönderilen Programlar */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Send className="h-5 w-5 text-blue-600" />
              Şubeye Gönderilen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {programs.filter(p => p.gonderen === 'yonetim').length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Send className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Henüz gönderilen program yok</p>
                </div>
              ) : (
                programs.filter(p => p.gonderen === 'yonetim').map((program, idx) => (
                  <div key={program.id || idx} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-blue-800">{program.baslik}</h4>
                        {program.aciklama && (
                          <p className="text-sm text-blue-600 mt-1">{program.aciklama}</p>
                        )}
                        <p className="text-xs text-blue-500 mt-2">
                          {new Date(program.tarih || program.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                      {program.okundu && (
                        <Badge variant="outline" className="text-green-600 border-green-300">Görüldü</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Alınan Programlar */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building className="h-5 w-5 text-green-600" />
              Şubeden Gelen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {programs.filter(p => p.gonderen === 'sube').length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Building className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Şubeden gelen program yok</p>
                </div>
              ) : (
                programs.filter(p => p.gonderen === 'sube').map((program, idx) => (
                  <div key={program.id || idx} className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-green-800">{program.baslik}</h4>
                        {program.aciklama && (
                          <p className="text-sm text-green-600 mt-1">{program.aciklama}</p>
                        )}
                        <p className="text-xs text-green-500 mt-2">
                          {new Date(program.tarih || program.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-green-600 border-green-300">Yeni</Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Takvim Bölümü */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-indigo-600" />
                <span className="text-xl">{getMonthName(currentMonth)}</span>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={prevMonth} className="h-8 w-8 p-0">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setCurrentMonth(new Date()); setSelectedDate(new Date()); }} className="h-8 px-3 text-xs">
                  Bugün
                </Button>
                <Button variant="outline" size="sm" onClick={nextMonth} className="h-8 w-8 p-0">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              showOutsideDays={true}
              className="rounded-md border w-full"
              classNames={{
                months: "flex flex-col w-full",
                month: "space-y-6 w-full",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-xl font-semibold hidden",
                nav: "hidden",
                table: "w-full border-collapse",
                head_row: "flex w-full",
                head_cell: "text-muted-foreground rounded-md flex-1 font-semibold text-base py-3",
                row: "flex w-full mt-2",
                cell: "relative flex-1 p-1 text-center text-sm",
                day: "h-12 w-full p-0 font-normal hover:bg-gray-100 rounded-md flex items-center justify-center",
                day_selected: "bg-primary text-primary-foreground hover:bg-primary",
                day_today: "bg-accent text-accent-foreground font-bold",
                day_outside: "text-muted-foreground opacity-50",
              }}
              modifiers={{ hasPlan: (date) => hasPlans(date) }}
              modifiersStyles={{
                hasPlan: { fontWeight: 'bold', backgroundColor: '#dbeafe', borderRadius: '8px' }
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-indigo-600" />
              {selectedDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDatePlans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Bu tarihte plan yok</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDatePlans.map((plan, index) => (
                  <div key={plan.id || index} className={cn("p-3 rounded-lg border", plan.tamamlandi ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200")}>
                    <div className="flex items-center gap-2 mb-1">
                      {plan.tamamlandi ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-blue-600" />}
                      <span className="font-semibold">{plan.baslik}</span>
                    </div>
                    {plan.aciklama && <p className="text-sm text-gray-600">{plan.aciklama}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Şube Yönetim İş Programı Tab İçeriği - BranchAssignments ile birebir aynı görünüm
  const renderSubeTab = () => (
    <div className="space-y-6" data-testid="sube-is-programi">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{branchName} - İş Programı</h2>
          <p className="text-sm text-gray-500">Günlük, aylık ve master iş planlarını görüntüleyin</p>
        </div>
        <Button onClick={loadSubeSchedules} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>

      {subeLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <Tabs value={subeMainTab} onValueChange={setSubeMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            {['daily', 'monthly', 'master'].map(type => {
              const Icon = getTabIcon(type);
              return (
                <TabsTrigger 
                  key={type} 
                  value={type} 
                  className="gap-2"
                  data-testid={`tab-${type}`}
                >
                  <Icon className="h-4 w-4" />
                  {getTabLabel(type)}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* GÜNLÜK İŞ PLANI */}
          <TabsContent value="daily" className="mt-4">
            {renderSubeScheduleTable()}
          </TabsContent>

          {/* AYLIK İŞ PLANI */}
          <TabsContent value="monthly" className="mt-4">
            {renderSubeScheduleTable()}
          </TabsContent>

          {/* MASTER İŞ PLANI */}
          <TabsContent value="master" className="mt-4">
            {renderSubeScheduleTable()}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-500))]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-is-programi">
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">
          {branchName} - İş Programı
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">
          Yönetim ve şube arası iş programı takibi
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="yonetim" className="gap-2">
            <Users className="h-4 w-4" />
            Yönetim İş Programı
          </TabsTrigger>
          <TabsTrigger value="sube" className="gap-2">
            <Building className="h-4 w-4" />
            Şube Yönetim İş Programı
          </TabsTrigger>
        </TabsList>

        <TabsContent value="yonetim" className="mt-6">
          {renderYonetimTab()}
        </TabsContent>

        <TabsContent value="sube" className="mt-6">
          {renderSubeTab()}
        </TabsContent>
      </Tabs>

      {/* Yeni İş Programı Ekleme Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni İş Programı Gönder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Başlık</Label>
              <Input
                value={newProgram.baslik}
                onChange={(e) => setNewProgram({ ...newProgram, baslik: e.target.value })}
                placeholder="İş programı başlığı"
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={newProgram.aciklama}
                onChange={(e) => setNewProgram({ ...newProgram, aciklama: e.target.value })}
                placeholder="İş programı detayları"
                rows={3}
              />
            </div>
            <div>
              <Label>Tarih</Label>
              <Input
                type="date"
                value={newProgram.tarih}
                onChange={(e) => setNewProgram({ ...newProgram, tarih: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>İptal</Button>
            <Button onClick={handleAddProgram} className="gap-2">
              <Send className="h-4 w-4" />
              Şubeye Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Planı Detay Modalı - BranchAssignments ile aynı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              İş Planı Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedSchedule && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-amber-100 text-amber-700">
                    {getTabLabel(selectedSchedule.schedule_type)}
                  </Badge>
                  {getStatusBadge(selectedSchedule.status)}
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedSchedule.title}</h3>
                {selectedSchedule.description && (
                  <p className="text-gray-600">{selectedSchedule.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="font-medium">{selectedSchedule.owner?.name || selectedSchedule.owner_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <p className="font-medium">
                    {selectedSchedule.created_at 
                      ? new Date(selectedSchedule.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Öncelik</Label>
                  <div className="mt-1">{getPriorityBadge(selectedSchedule.priority)}</div>
                </div>
                {selectedSchedule.start_date && (
                  <div>
                    <Label className="text-gray-500">Başlangıç</Label>
                    <p className="font-medium">{new Date(selectedSchedule.start_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
                {selectedSchedule.end_date && (
                  <div>
                    <Label className="text-gray-500">Bitiş</Label>
                    <p className="font-medium">{new Date(selectedSchedule.end_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {selectedSchedule.documents && selectedSchedule.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Ekli Belgeler</Label>
                  <div className="space-y-2">
                    {selectedSchedule.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = doc.data;
                              link.download = doc.name;
                              link.click();
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kabul Edenler */}
              {selectedSchedule.accepted_by && selectedSchedule.accepted_by.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Kabul Edenler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedSchedule.accepted_by.map((a, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">
                        {a.user_name} - {new Date(a.accepted_at).toLocaleDateString('tr-TR')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto">
            {previewDoc?.type?.startsWith('image/') ? (
              <img 
                src={previewDoc?.data} 
                alt={previewDoc?.name}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
              />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.data}
                title={previewDoc?.name}
                className="w-full h-[65vh] border-0 rounded"
              />
            ) : (
              <p className="text-gray-500">Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewDoc?.data;
                link.download = previewDoc?.name;
                link.click();
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewDoc(null)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Güncel Durum Modal */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Güncel Durum - {selectedSchedule?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Label className="font-semibold">Güncelleme Geçmişi</Label>
            {progressLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Henüz güncelleme yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-3">
                {progressLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{log.description}</p>
                    {log.documents && log.documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                        {log.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-gray-500" />
                            <span className="text-xs">{doc.name}</span>
                            {isPreviewable(doc.type) && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-blue-500 hover:text-blue-700 ml-1"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name;
                                link.click();
                              }}
                              className="text-green-500 hover:text-green-700 ml-1"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardIsPlan;
